"use client"

import * as React from "react"
import { Link, useNavigate } from "react-router"
import { ChartDataTable, ChartFigure } from "@/components/charts-core"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { STATUS_BADGE_TONE_CLASS } from "@/lib/list-status-badges"
import type { EvaluationCounts } from "@/lib/mock/evaluation-dashboard"

type PipelineStage = {
  id: string
  label: string
  count: number
  colorVar: string
}

export const STAGE_META = {
  setup: { label: "Setup", colorVar: "var(--chart-4)" },
  scheduled: { label: "Scheduled", colorVar: "var(--primary)" },
  live: { label: "Live", colorVar: "var(--brand-color)" },
  closed: { label: "Closed", colorVar: "var(--chart-2)" },
  published: { label: "Published", colorVar: "var(--chart-3)" },
} satisfies Record<string, { label: string; colorVar: string }>

function buildStages(counts: EvaluationCounts): PipelineStage[] {
  const setup = counts.notSetup + counts.draft
  const stages: PipelineStage[] = [
    { id: "setup", ...STAGE_META.setup, count: setup },
    { id: "scheduled", ...STAGE_META.scheduled, count: counts.scheduled },
    { id: "live", ...STAGE_META.live, count: counts.live },
    { id: "closed", ...STAGE_META.closed, count: counts.closed },
  ]
  if (counts.published > 0) {
    stages.push({ id: "published", ...STAGE_META.published, count: counts.published })
  }
  return stages.filter(stage => stage.count > 0)
}

interface EvaluationStagePipelineProps {
  counts: EvaluationCounts
  courseOfferings: number
  className?: string
  /** Persistent color dot + label + count row under the bar, not just on hover. */
  showLegend?: boolean
  /** Stage currently cross-filtering a companion table, if any. */
  activeStageId?: string | null
  /** Called when a clickable segment/legend entry is toggled. Omit to keep the bar passive. */
  onStageSelect?: (stageId: string) => void
  /** Stages with a matching row to filter to. Segments outside this set render but aren't clickable. */
  selectableStageIds?: ReadonlySet<string>
}

/**
 * Cross-stage distribution bar. Only renders when a term has courses split
 * across 2+ stages — a single-stage term's count and action already live in
 * the work-queue table row below, so a 100% bar here would just restate it.
 */
export function EvaluationStagePipeline({
  counts,
  courseOfferings,
  className,
  showLegend = false,
  activeStageId = null,
  onStageSelect,
  selectableStageIds,
}: EvaluationStagePipelineProps) {
  const stages = React.useMemo(() => buildStages(counts), [counts])
  const total = Math.max(
    courseOfferings,
    stages.reduce((sum, stage) => sum + stage.count, 0),
    1,
  )

  if (stages.length < 2) {
    return null
  }

  const summary = stages.map(stage => `${stage.count} ${stage.label.toLowerCase()}`).join(", ")
  const isInteractive = Boolean(onStageSelect)

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div
        className="flex h-3 w-full gap-0.5"
        role={isInteractive ? "group" : "img"}
        aria-label={summary}
      >
        {stages.map((stage, index) => {
          const isActive = activeStageId === stage.id
          const isSelectable = Boolean(onStageSelect) && (!selectableStageIds || selectableStageIds.has(stage.id))
          const dimmed = activeStageId != null && !isActive
          const segmentClassName = cn(
            "min-w-0 transition-opacity",
            index === 0 && "rounded-l-full",
            index === stages.length - 1 && "rounded-r-full",
            dimmed && "opacity-35",
          )
          const segmentStyle = { flexGrow: stage.count, flexBasis: 0, backgroundColor: stage.colorVar }

          return (
            <Tooltip key={stage.id}>
              <TooltipTrigger asChild>
                {isSelectable ? (
                  <button
                    type="button"
                    aria-pressed={isActive}
                    aria-label={`${stage.label}, ${stage.count} courses`}
                    className={cn(
                      segmentClassName,
                      "cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                    )}
                    style={segmentStyle}
                    onClick={() => onStageSelect?.(stage.id)}
                  />
                ) : (
                  <div className={segmentClassName} style={segmentStyle} />
                )}
              </TooltipTrigger>
              <TooltipContent side="top">
                {stage.count} {stage.label} · {Math.round((stage.count / total) * 100)}%
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
      {showLegend ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {stages.map(stage => {
            const isActive = activeStageId === stage.id
            const isSelectable = Boolean(onStageSelect) && (!selectableStageIds || selectableStageIds.has(stage.id))
            const dimmed = activeStageId != null && !isActive
            const entryClassName = cn(
              "inline-flex items-center gap-1.5 rounded-full text-sm text-muted-foreground transition-opacity",
              dimmed && "opacity-50",
            )
            const entry = (
              <>
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: stage.colorVar }}
                  aria-hidden="true"
                />
                <span className="text-foreground">{stage.count}</span> {stage.label}
              </>
            )

            if (!isSelectable) {
              return (
                <span key={stage.id} className={entryClassName}>
                  {entry}
                </span>
              )
            }

            return (
              <button
                key={stage.id}
                type="button"
                aria-pressed={isActive}
                onClick={() => onStageSelect?.(stage.id)}
                className={cn(
                  entryClassName,
                  "cursor-pointer outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                  isActive && "text-foreground",
                )}
              >
                {entry}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

interface ResponseTargetBarProps {
  rate: number
  target?: number
  closingLabel?: string
  className?: string
}

/** Live-window response fill against the institutional target. */
export function ResponseTargetBar({
  rate,
  target = 70,
  closingLabel,
  className,
}: ResponseTargetBarProps) {
  const fill = Math.max(0, Math.min(100, rate))

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-medium text-foreground">Live response</span>
        <span className="tabular-nums text-muted-foreground">
          {rate}% · target {target}%
          {closingLabel ? ` · ${closingLabel}` : ""}
        </span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={fill}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Live response ${rate} percent`}
      >
        <div
          className={cn(
            "h-full rounded-full transition-all",
            rate >= target ? "bg-chart-2" : rate >= 50 ? "bg-chart-4" : "bg-destructive",
          )}
          style={{ width: `${fill}%` }}
        />
      </div>
    </div>
  )
}

type LabeledLifecycleStageId = keyof EvaluationCounts

const LABELED_LIFECYCLE_STAGES: {
  id: LabeledLifecycleStageId
  label: string
  icon: string
  toneClass: string
  phase: "setup" | "collection" | "results"
}[] = [
  {
    id: "notSetup",
    label: "Not set up",
    icon: "fa-clipboard-list",
    toneClass: STATUS_BADGE_TONE_CLASS.danger,
    phase: "setup",
  },
  {
    id: "draft",
    label: "Draft",
    icon: "fa-file-pen",
    toneClass: STATUS_BADGE_TONE_CLASS.warning,
    phase: "setup",
  },
  {
    id: "scheduled",
    label: "Scheduled",
    icon: "fa-calendar-clock",
    toneClass: STATUS_BADGE_TONE_CLASS.info,
    phase: "setup",
  },
  {
    id: "live",
    label: "Collecting",
    icon: "fa-signal-stream",
    toneClass:
      "bg-[var(--icon-disc-brand-bg)] text-[var(--icon-disc-brand-fg)]",
    phase: "collection",
  },
  {
    id: "closed",
    label: "Closed",
    icon: "fa-inbox",
    toneClass: STATUS_BADGE_TONE_CLASS.success,
    phase: "results",
  },
  {
    id: "published",
    label: "Published",
    icon: "fa-circle-check",
    toneClass: STATUS_BADGE_TONE_CLASS.neutral,
    phase: "results",
  },
]

const LIFECYCLE_PHASES: { id: "setup" | "collection" | "results"; label: string }[] = [
  { id: "setup", label: "Setup" },
  { id: "collection", label: "Collection" },
  { id: "results", label: "Results" },
]

type LifecycleStage = {
  id: LabeledLifecycleStageId
  label: string
  icon: string
  toneClass: string
  phase: "setup" | "collection" | "results"
  count: number
}

type LifecycleDisplaySegment =
  | { kind: "stage"; stage: LifecycleStage }
  | { kind: "zero-single"; stage: LifecycleStage }
  | { kind: "zero-collapsed"; stages: LifecycleStage[] }
  | { kind: "phase-empty"; phase: LifecycleStage["phase"] }

const LIFECYCLE_SEGMENT_MIN_WIDTH_PX = 78
const LIFECYCLE_ZERO_SINGLE_WIDTH = "3.875rem"
const LIFECYCLE_ZERO_COLLAPSED_WIDTH = "6.75rem"
const LIFECYCLE_PHASE_EMPTY_MIN_WIDTH_PX = 140

function phaseEmptyMessage(phase: LifecycleStage["phase"]): string {
  if (phase === "setup") return "No setup yet"
  if (phase === "collection") return "No collections yet"
  return "No results yet"
}

function phasesWithAllZeros(stages: LifecycleStage[]): Set<LifecycleStage["phase"]> {
  const emptyPhases = new Set<LifecycleStage["phase"]>()
  for (const phase of LIFECYCLE_PHASES) {
    const phaseStages = stages.filter(stage => stage.phase === phase.id)
    if (phaseStages.length > 0 && phaseStages.every(stage => stage.count === 0)) {
      emptyPhases.add(phase.id)
    }
  }
  return emptyPhases
}

function buildLifecycleDisplaySegments(stages: LifecycleStage[]): LifecycleDisplaySegment[] {
  const allEmptyPhases = phasesWithAllZeros(stages)
  const segments: LifecycleDisplaySegment[] = []
  const emittedPhaseEmpty = new Set<LifecycleStage["phase"]>()

  for (let index = 0; index < stages.length; ) {
    const stage = stages[index]!

    if (allEmptyPhases.has(stage.phase)) {
      if (!emittedPhaseEmpty.has(stage.phase)) {
        segments.push({ kind: "phase-empty", phase: stage.phase })
        emittedPhaseEmpty.add(stage.phase)
      }
      while (index < stages.length && stages[index]!.phase === stage.phase) {
        index += 1
      }
      continue
    }

    if (stage.count > 0) {
      segments.push({ kind: "stage", stage })
      index += 1
      continue
    }

    const zeroRun: LifecycleStage[] = []
    while (
      index < stages.length &&
      stages[index]!.count === 0 &&
      !allEmptyPhases.has(stages[index]!.phase)
    ) {
      zeroRun.push(stages[index]!)
      index += 1
    }

    if (zeroRun.length === 1) {
      segments.push({ kind: "zero-single", stage: zeroRun[0]! })
    } else if (zeroRun.length > 1) {
      segments.push({ kind: "zero-collapsed", stages: zeroRun })
    }
  }

  return segments
}

function segmentPhase(segment: LifecycleDisplaySegment): LifecycleStage["phase"] {
  if (segment.kind === "phase-empty") return segment.phase
  if (segment.kind === "zero-collapsed") return segment.stages[0]!.phase
  return segment.stage.phase
}

function segmentFlexStyle(segment: LifecycleDisplaySegment): React.CSSProperties {
  switch (segment.kind) {
    case "stage":
      return { flex: `${segment.stage.count} 1 0`, minWidth: LIFECYCLE_SEGMENT_MIN_WIDTH_PX }
    case "zero-single":
      return { flex: `0 0 ${LIFECYCLE_ZERO_SINGLE_WIDTH}` }
    case "zero-collapsed":
      return { flex: `0 0 ${LIFECYCLE_ZERO_COLLAPSED_WIDTH}` }
    case "phase-empty":
      return { flex: "1 1 0", minWidth: LIFECYCLE_PHASE_EMPTY_MIN_WIDTH_PX }
  }
}

function segmentCompactFlexStyle(segment: LifecycleDisplaySegment): React.CSSProperties {
  switch (segment.kind) {
    case "stage":
      return { flex: `${Math.max(segment.stage.count, 1)} 1 0`, minWidth: 4 }
    case "zero-single":
      return { flex: "0 0 8px" }
    case "zero-collapsed":
      return { flex: "0 0 12px" }
    case "phase-empty": {
      const span = LABELED_LIFECYCLE_STAGES.filter(row => row.phase === segment.phase).length
      return { flex: `${span} 1 0`, minWidth: 8 }
    }
  }
}

function lifecycleSegmentAriaLabel(
  segment: LifecycleDisplaySegment,
  courseOfferings: number,
): string {
  switch (segment.kind) {
    case "stage": {
      const coursesOf = lifecycleCoursesOf(segment.stage.count, courseOfferings)
      const percent = Math.round((segment.stage.count / Math.max(courseOfferings, 1)) * 100)
      return `${segment.stage.label}. ${coursesOf}. ${percent}%`
    }
    case "zero-single": {
      const coursesOf = lifecycleCoursesOf(0, courseOfferings)
      return `${segment.stage.label}. ${coursesOf}.`
    }
    case "zero-collapsed": {
      const names = segment.stages.map(stage => stage.label).join(", ")
      return `${segment.stages.length} empty stages: ${names}`
    }
    case "phase-empty":
      return phaseEmptyMessage(segment.phase)
  }
}

const LIFECYCLE_ZERO_SHELL_CLASS =
  "border border-dashed border-border/80 bg-muted/30 text-muted-foreground"

/** Matches CHART_KBD_ACTIVE_BAR: outer stroke + lift like Recharts activeBar. */
function lifecycleSegmentEmphasisClass(
  index: number,
  activeIndex: number | null,
  hoverIndex: number | null,
): string {
  const emphasized = index === activeIndex || index === hoverIndex
  const hasFocus = activeIndex !== null || hoverIndex !== null
  return cn(
    "rounded-[inherit] transition-opacity",
    hasFocus && !emphasized && "opacity-[0.72]",
    emphasized && "relative z-10 ring-2 ring-ring ring-offset-1 ring-offset-background",
  )
}

function lifecycleSegmentHref(
  segment: LifecycleDisplaySegment,
  stageHref: (stageId: LabeledLifecycleStageId) => string,
): string | null {
  if (segment.kind === "stage" || segment.kind === "zero-single") {
    return stageHref(segment.stage.id)
  }
  return null
}

function lifecycleChartTableRows(
  segments: LifecycleDisplaySegment[],
  courseOfferings: number,
): (string | number)[][] {
  const total = Math.max(courseOfferings, 1)
  return segments.map(segment => {
    switch (segment.kind) {
      case "stage": {
        const percent = Math.round((segment.stage.count / total) * 100)
        return [segment.stage.label, segment.stage.count, `${percent}%`]
      }
      case "zero-single":
        return [segment.stage.label, 0, "0%"]
      case "zero-collapsed":
        return [
          segment.stages.map(stage => stage.label).join(", "),
          0,
          "0%",
        ]
      case "phase-empty":
        return [phaseEmptyMessage(segment.phase), 0, "0%"]
    }
  })
}

function lifecycleSegmentKey(segment: LifecycleDisplaySegment, index: number): string {
  switch (segment.kind) {
    case "stage":
      return segment.stage.id
    case "zero-single":
      return segment.stage.id
    case "zero-collapsed":
      return segment.stages.map(stage => stage.id).join("-")
    case "phase-empty":
      return `phase-empty-${segment.phase}-${index}`
  }
}

function zeroStageHint(stage: LifecycleStage, orderedStages: LifecycleStage[]): string {
  const stageIndex = orderedStages.findIndex(row => row.id === stage.id)
  const nextPopulated = orderedStages.slice(stageIndex + 1).find(row => row.count > 0)
  if (nextPopulated) return `All moved on to ${nextPopulated.label}`
  if (stage.id === "published") return "Not published yet"
  return "No courses in this stage"
}

function lifecycleCoursesOf(count: number, courseOfferings: number): string {
  return courseOfferings === 1
    ? `${count} of 1 course`
    : `${count} of ${courseOfferings} courses`
}

/** ChartFigure skips focus on `<a>` / `<button>` — refocus chart shell so arrow keys keep working. */
function focusParentChartFigure(element: HTMLElement) {
  const figure = element.closest<HTMLElement>('[role="application"]')
  if (figure) queueMicrotask(() => figure.focus())
}

function LifecycleStageLabelRow({
  stage,
  className,
  iconClassName,
}: {
  stage: LifecycleStage
  className?: string
  iconClassName?: string
}) {
  return (
    <p className={cn("flex min-w-0 items-center gap-1.5 truncate leading-tight", className)}>
      <i className={cn("fa-light shrink-0", stage.icon, iconClassName ?? "text-xs")} aria-hidden />
      <span className="truncate">{stage.label}</span>
    </p>
  )
}

function LifecycleStageHoverCard({
  stage,
  courseOfferings,
  href,
  index,
  activeIndex,
  hoverIndex,
  onSegmentHover,
  children,
  triggerClassName,
}: {
  stage: LifecycleStage
  courseOfferings: number
  href: string
  index: number
  activeIndex: number | null
  hoverIndex: number | null
  onSegmentHover: (index: number | null) => void
  children: React.ReactNode
  triggerClassName?: string
}) {
  const total = Math.max(courseOfferings, 1)
  const percent = Math.round((stage.count / total) * 100)
  const coursesOf = lifecycleCoursesOf(stage.count, courseOfferings)
  const ariaLabel =
    stage.count === 0
      ? `${stage.label}. ${coursesOf}.`
      : `${stage.label}. ${coursesOf}. ${percent}%`
  const keyboardSelected = activeIndex === index
  const [hoverOpen, setHoverOpen] = React.useState(false)
  const open = keyboardSelected || hoverOpen

  return (
    <HoverCard open={open} onOpenChange={setHoverOpen}>
      <HoverCardTrigger asChild>
        <Link
          to={href}
          tabIndex={-1}
          aria-label={ariaLabel}
          onPointerDown={event => focusParentChartFigure(event.currentTarget)}
          onPointerEnter={() => {
            onSegmentHover(index)
            setHoverOpen(true)
          }}
          onPointerLeave={() => {
            onSegmentHover(null)
            setHoverOpen(false)
          }}
          className={cn(
            "block h-full cursor-pointer outline-none",
            triggerClassName ?? "min-h-16 rounded-lg",
          )}
        >
          <div className={cn("h-full", lifecycleSegmentEmphasisClass(index, activeIndex, hoverIndex))}>
            {children}
          </div>
        </Link>
      </HoverCardTrigger>
      <HoverCardContent side="top" className="z-50 w-52">
        <p className="flex items-center gap-1.5 font-medium text-foreground">
          <i className={cn("fa-light shrink-0 text-xs", stage.icon)} aria-hidden />
          {stage.label}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">{coursesOf}</p>
        {stage.count > 0 ? (
          <p className="text-sm tabular-nums text-muted-foreground">{percent}%</p>
        ) : null}
      </HoverCardContent>
    </HoverCard>
  )
}

function LifecyclePopulatedSegment({
  stage,
  courseOfferings,
  stageHref,
  index,
  activeIndex,
  hoverIndex,
  onSegmentHover,
}: {
  stage: LifecycleStage
  courseOfferings: number
  stageHref: (stageId: LabeledLifecycleStageId) => string
  index: number
  activeIndex: number | null
  hoverIndex: number | null
  onSegmentHover: (index: number | null) => void
}) {
  return (
    <LifecycleStageHoverCard
      stage={stage}
      courseOfferings={courseOfferings}
      href={stageHref(stage.id)}
      index={index}
      activeIndex={activeIndex}
      hoverIndex={hoverIndex}
      onSegmentHover={onSegmentHover}
    >
      <div
        className={cn(
          "flex h-full min-h-16 flex-col justify-center gap-0.5 rounded-lg px-3 py-3",
          stage.toneClass,
        )}
      >
        <p className="text-xl font-medium tabular-nums">{stage.count}</p>
        <LifecycleStageLabelRow stage={stage} className="text-xs opacity-80" />
      </div>
    </LifecycleStageHoverCard>
  )
}

function LifecycleZeroSingleSegment({
  stage,
  courseOfferings,
  stageHref,
  index,
  activeIndex,
  hoverIndex,
  onSegmentHover,
}: {
  stage: LifecycleStage
  courseOfferings: number
  stageHref: (stageId: LabeledLifecycleStageId) => string
  index: number
  activeIndex: number | null
  hoverIndex: number | null
  onSegmentHover: (index: number | null) => void
}) {
  return (
    <LifecycleStageHoverCard
      stage={stage}
      courseOfferings={courseOfferings}
      href={stageHref(stage.id)}
      index={index}
      activeIndex={activeIndex}
      hoverIndex={hoverIndex}
      onSegmentHover={onSegmentHover}
    >
      <div
        className={cn(
          "flex h-full min-h-16 flex-col justify-center rounded-lg px-2.5 py-3",
          LIFECYCLE_ZERO_SHELL_CLASS,
        )}
      >
        <p className="text-xl font-medium tabular-nums leading-none">0</p>
        <LifecycleStageLabelRow
          stage={stage}
          className="mt-1 text-[11px] text-muted-foreground"
          iconClassName="text-[10px]"
        />
      </div>
    </LifecycleStageHoverCard>
  )
}

function LifecycleZeroCollapsedSegment({
  stages,
  orderedStages,
  stageHref,
  index,
  activeIndex,
  hoverIndex,
  onSegmentHover,
}: {
  stages: LifecycleStage[]
  orderedStages: LifecycleStage[]
  stageHref: (stageId: LabeledLifecycleStageId) => string
  index: number
  activeIndex: number | null
  hoverIndex: number | null
  onSegmentHover: (index: number | null) => void
}) {
  const emptyLabel =
    stages.length === 1 ? "1 stage empty" : `${stages.length} stages empty`

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          tabIndex={-1}
          aria-label={`${emptyLabel}. Show empty stages.`}
          onPointerDown={event => focusParentChartFigure(event.currentTarget)}
          onPointerEnter={() => onSegmentHover(index)}
          onPointerLeave={() => onSegmentHover(null)}
          className={cn(
            "flex h-full min-h-16 w-full flex-col justify-center rounded-lg px-2.5 py-3 text-left outline-none",
            lifecycleSegmentEmphasisClass(index, activeIndex, hoverIndex),
            LIFECYCLE_ZERO_SHELL_CLASS,
          )}
        >
          <p className="text-xl font-medium tabular-nums leading-none">0</p>
          <p className="mt-1 flex items-center gap-0.5 text-[11px] leading-tight">
            <span className="truncate">{emptyLabel}</span>
            <i className="fa-light fa-chevron-down shrink-0 text-[10px]" aria-hidden />
          </p>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[18.75rem] p-1">
        <ul className="flex flex-col">
          {stages.map(stage => (
            <li key={stage.id}>
              <Link
                to={stageHref(stage.id)}
                className="flex items-baseline gap-3 rounded-md px-3 py-2.5 outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="min-w-3.5 text-base tabular-nums text-muted-foreground">0</span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    <i className={cn("fa-light shrink-0 text-xs", stage.icon)} aria-hidden />
                    {stage.label}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {zeroStageHint(stage, orderedStages)}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}

function LifecyclePhaseEmptySegment({
  phase,
  index,
  activeIndex,
  hoverIndex,
  onSegmentHover,
}: {
  phase: LifecycleStage["phase"]
  index: number
  activeIndex: number | null
  hoverIndex: number | null
  onSegmentHover: (index: number | null) => void
}) {
  const keyboardSelected = activeIndex === index
  const [hoverOpen, setHoverOpen] = React.useState(false)
  const open = keyboardSelected || hoverOpen

  return (
    <HoverCard open={open} onOpenChange={setHoverOpen}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          tabIndex={-1}
          aria-label={phaseEmptyMessage(phase)}
          onPointerDown={event => focusParentChartFigure(event.currentTarget)}
          onPointerEnter={() => {
            onSegmentHover(index)
            setHoverOpen(true)
          }}
          onPointerLeave={() => {
            onSegmentHover(null)
            setHoverOpen(false)
          }}
          className={cn(
            "flex h-full min-h-16 w-full items-center rounded-lg bg-muted px-3 py-3 text-left outline-none",
            lifecycleSegmentEmphasisClass(index, activeIndex, hoverIndex),
          )}
        >
          <p className="text-xs text-muted-foreground">{phaseEmptyMessage(phase)}</p>
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="top" className="z-50 w-52">
        <p className="text-sm text-muted-foreground">{phaseEmptyMessage(phase)}</p>
      </HoverCardContent>
    </HoverCard>
  )
}

function LifecycleCompactSegment({
  segment,
  index,
  segmentCount,
  courseOfferings,
  orderedStages,
  stageHref,
  activeIndex,
  hoverIndex,
  onSegmentHover,
}: {
  segment: LifecycleDisplaySegment
  index: number
  segmentCount: number
  courseOfferings: number
  orderedStages: LifecycleStage[]
  stageHref: (stageId: LabeledLifecycleStageId) => string
  activeIndex: number | null
  hoverIndex: number | null
  onSegmentHover: (index: number | null) => void
}) {
  const roundedLeft = index === 0
  const roundedRight = index === segmentCount - 1
  const segmentClassName = cn(
    "min-h-6 min-w-0 self-stretch rounded-sm",
    roundedLeft && "rounded-l-full",
    roundedRight && "rounded-r-full",
  )

  if (segment.kind === "stage") {
    return (
      <LifecycleStageHoverCard
        stage={segment.stage}
        courseOfferings={courseOfferings}
        href={stageHref(segment.stage.id)}
        index={index}
        activeIndex={activeIndex}
        hoverIndex={hoverIndex}
        onSegmentHover={onSegmentHover}
        triggerClassName={cn("min-h-6 w-full", segmentClassName)}
      >
        <div className={cn("h-3 w-full", segment.stage.toneClass)} />
      </LifecycleStageHoverCard>
    )
  }

  if (segment.kind === "zero-single") {
    return (
      <LifecycleStageHoverCard
        stage={segment.stage}
        courseOfferings={courseOfferings}
        href={stageHref(segment.stage.id)}
        index={index}
        activeIndex={activeIndex}
        hoverIndex={hoverIndex}
        onSegmentHover={onSegmentHover}
        triggerClassName={cn("min-h-6 w-full", segmentClassName)}
      >
        <div className={cn("h-3 w-full", LIFECYCLE_ZERO_SHELL_CLASS)} />
      </LifecycleStageHoverCard>
    )
  }

  if (segment.kind === "zero-collapsed") {
    const emptyLabel =
      segment.stages.length === 1 ? "1 stage empty" : `${segment.stages.length} stages empty`

    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            tabIndex={-1}
            aria-label={`${emptyLabel}. Show empty stages.`}
            onPointerDown={event => focusParentChartFigure(event.currentTarget)}
            onPointerEnter={() => onSegmentHover(index)}
            onPointerLeave={() => onSegmentHover(null)}
            className={cn(
              segmentClassName,
              "h-3 w-full cursor-pointer outline-none",
              lifecycleSegmentEmphasisClass(index, activeIndex, hoverIndex),
              LIFECYCLE_ZERO_SHELL_CLASS,
            )}
          />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[18.75rem] p-1">
          <ul className="flex flex-col">
            {segment.stages.map(stage => (
              <li key={stage.id}>
                <Link
                  to={stageHref(stage.id)}
                  className="flex items-baseline gap-3 rounded-md px-3 py-2.5 outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="min-w-3.5 text-base tabular-nums text-muted-foreground">0</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                      <i className={cn("fa-light shrink-0 text-xs", stage.icon)} aria-hidden />
                      {stage.label}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {zeroStageHint(stage, orderedStages)}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    )
  }

  const keyboardSelected = activeIndex === index
  const [hoverOpen, setHoverOpen] = React.useState(false)
  const open = keyboardSelected || hoverOpen

  return (
    <HoverCard open={open} onOpenChange={setHoverOpen}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          tabIndex={-1}
          aria-label={phaseEmptyMessage(segment.phase)}
          onPointerDown={event => focusParentChartFigure(event.currentTarget)}
          onPointerEnter={() => {
            onSegmentHover(index)
            setHoverOpen(true)
          }}
          onPointerLeave={() => {
            onSegmentHover(null)
            setHoverOpen(false)
          }}
          className={cn(
            segmentClassName,
            "h-3 w-full cursor-default bg-muted/80 outline-none",
            lifecycleSegmentEmphasisClass(index, activeIndex, hoverIndex),
          )}
        />
      </HoverCardTrigger>
      <HoverCardContent side="top" className="w-52">
        <p className="text-sm text-muted-foreground">{phaseEmptyMessage(segment.phase)}</p>
      </HoverCardContent>
    </HoverCard>
  )
}

function LifecycleCompactProportionBar({
  segments,
  stages,
  courseOfferings,
  stageHref,
  activeIndex,
  hoverIndex,
  onSegmentHover,
  className,
}: {
  segments: LifecycleDisplaySegment[]
  stages: LifecycleStage[]
  courseOfferings: number
  stageHref: (stageId: LabeledLifecycleStageId) => string
  activeIndex: number | null
  hoverIndex: number | null
  onSegmentHover: (index: number | null) => void
  className?: string
}) {
  return (
    <div
      className={cn(
        "flex h-6 w-full items-center gap-0.5 overflow-visible py-0.5 md:hidden",
        className,
      )}
    >
      {segments.map((segment, index) => (
        <div
          key={lifecycleSegmentKey(segment, index)}
          className="flex min-h-6 min-w-0 items-center"
          style={segmentCompactFlexStyle(segment)}
        >
          <LifecycleCompactSegment
            segment={segment}
            index={index}
            segmentCount={segments.length}
            courseOfferings={courseOfferings}
            orderedStages={stages}
            stageHref={stageHref}
            activeIndex={activeIndex}
            hoverIndex={hoverIndex}
            onSegmentHover={onSegmentHover}
          />
        </div>
      ))}
    </div>
  )
}

/** Six-stage labeled mix for Operations. Collapses zero runs; one slot per empty phase group. */
export function EvaluationLabeledLifecycle({
  counts,
  courseOfferings,
  stageHref,
  className,
  density = "default",
}: {
  counts: EvaluationCounts
  courseOfferings: number
  stageHref: (stageId: LabeledLifecycleStageId) => string
  className?: string
  /** Narrow columns: proportion bar only (no wide segment cards). */
  density?: "default" | "compact"
}) {
  const navigate = useNavigate()
  const [hoverIndex, setHoverIndex] = React.useState<number | null>(null)
  const stages = React.useMemo(
    () =>
      LABELED_LIFECYCLE_STAGES.map(stage => ({
        ...stage,
        count: counts[stage.id],
      })),
    [counts],
  )
  const segments = React.useMemo(() => buildLifecycleDisplaySegments(stages), [stages])
  const summary = React.useMemo(
    () => segments.map(segment => lifecycleSegmentAriaLabel(segment, courseOfferings)).join(". "),
    [segments, courseOfferings],
  )

  function activateSegment(index: number) {
    const segment = segments[index]
    if (!segment) return
    const href = lifecycleSegmentHref(segment, stageHref)
    if (href) navigate(href)
  }

  return (
    <div className={cn("w-full min-w-0", className)}>
      <ChartFigure
        label="Course lifecycle by stage"
        summary={summary}
        dataLength={segments.length}
      >
        {activeIndex => (
          <>
            <div
              onKeyDown={event => {
                if (event.key !== "Enter" || activeIndex === null) return
                event.preventDefault()
                activateSegment(activeIndex)
              }}
            >
              <LifecycleCompactProportionBar
                segments={segments}
                stages={stages}
                courseOfferings={courseOfferings}
                stageHref={stageHref}
                activeIndex={activeIndex}
                hoverIndex={hoverIndex}
                onSegmentHover={setHoverIndex}
                className={density === "compact" ? "flex pt-0.5 md:flex" : undefined}
              />
              <div
                className={cn(
                  "hidden w-full flex-col gap-2 overflow-visible md:flex",
                  density === "compact" && "md:hidden",
                )}
              >
                <div className="flex w-full gap-1.5 overflow-visible py-0.5">
                  {segments.map((segment, index) => (
                    <div
                      key={lifecycleSegmentKey(segment, index)}
                      className="min-w-0"
                      style={segmentFlexStyle(segment)}
                    >
                      {segment.kind === "stage" ? (
                        <LifecyclePopulatedSegment
                          stage={segment.stage}
                          courseOfferings={courseOfferings}
                          stageHref={stageHref}
                          index={index}
                          activeIndex={activeIndex}
                          hoverIndex={hoverIndex}
                          onSegmentHover={setHoverIndex}
                        />
                      ) : null}
                      {segment.kind === "zero-single" ? (
                        <LifecycleZeroSingleSegment
                          stage={segment.stage}
                          courseOfferings={courseOfferings}
                          stageHref={stageHref}
                          index={index}
                          activeIndex={activeIndex}
                          hoverIndex={hoverIndex}
                          onSegmentHover={setHoverIndex}
                        />
                      ) : null}
                      {segment.kind === "zero-collapsed" ? (
                        <LifecycleZeroCollapsedSegment
                          stages={segment.stages}
                          orderedStages={stages}
                          stageHref={stageHref}
                          index={index}
                          activeIndex={activeIndex}
                          hoverIndex={hoverIndex}
                          onSegmentHover={setHoverIndex}
                        />
                      ) : null}
                      {segment.kind === "phase-empty" ? (
                        <LifecyclePhaseEmptySegment
                          phase={segment.phase}
                          index={index}
                          activeIndex={activeIndex}
                          hoverIndex={hoverIndex}
                          onSegmentHover={setHoverIndex}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
                <div className="flex w-full gap-1.5 text-xs font-medium text-muted-foreground">
                  {segments.map((segment, index) => {
                    const phase = segmentPhase(segment)
                    const prevPhase = index > 0 ? segmentPhase(segments[index - 1]!) : null
                    return (
                      <div
                        key={`${lifecycleSegmentKey(segment, index)}-phase`}
                        className="min-w-0 truncate"
                        style={segmentFlexStyle(segment)}
                      >
                        {phase !== prevPhase
                          ? LIFECYCLE_PHASES.find(row => row.id === phase)?.label
                          : null}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
            <ChartDataTable
              caption="Course lifecycle by stage"
              headers={["Stage", "Courses", "Share"]}
              rows={lifecycleChartTableRows(segments, courseOfferings)}
            />
          </>
        )}
      </ChartFigure>
    </div>
  )
}

export { evaluationCoverageCount } from "@/lib/mock/evaluation-dashboard"
